import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

const ROLES = ["super_admin", "admin", "sales", "content_editor"];

export default function Users() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "admin", password: "", status: "active" });

  const load = async () => {
    const { data } = await api.get("/admin/users");
    setUsers(data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/admin/users", form);
      toast.success("User created");
      setCreating(false);
      setForm({ email: "", name: "", role: "admin", password: "", status: "active" });
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const updateRole = async (id, role) => {
    await api.patch(`/admin/users/${id}`, { role });
    toast.success("Role updated");
    await load();
  };
  const toggleStatus = async (u) => {
    await api.patch(`/admin/users/${u.id}`, { status: u.status === "active" ? "disabled" : "active" });
    await load();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("Deleted");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const canManage = currentUser?.role === "super_admin";

  return (
    <div className="p-8 md:p-10" data-testid="admin-users">
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="overline mb-2">Team</p>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>Admin users</h1>
        </div>
        {canManage && (
          <button onClick={() => setCreating(true)} className="btn-signal" data-testid="users-create">
            <Plus size={16} weight="bold" /> Add user
          </button>
        )}
      </div>

      <div className="bg-white border border-zinc-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50">
            <tr className="text-left">
              {["Name", "Email", "Role", "Status", "Created", ""].map((h) => (
                <th key={h} className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-zinc-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-zinc-200 hover:bg-zinc-50" data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-3 font-medium">{u.name}</td>
                <td className="px-4 py-3 text-zinc-600">{u.email}</td>
                <td className="px-4 py-3">
                  {canManage && u.id !== currentUser?.id ? (
                    <select value={u.role} onChange={(e) => updateRole(u.id, e.target.value)} className="border border-zinc-200 px-2 py-1 text-xs bg-white">
                      {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  ) : (
                    <span className="status-pill">{u.role}</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {canManage && u.id !== currentUser?.id ? (
                    <button onClick={() => toggleStatus(u)} className="status-pill">{u.status}</button>
                  ) : (
                    <span className="status-pill">{u.status}</span>
                  )}
                </td>
                <td className="px-4 py-3 text-xs font-mono text-zinc-500">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                <td className="px-4 py-3 text-right">
                  {canManage && u.id !== currentUser?.id && (
                    <button onClick={() => remove(u.id)} className="text-zinc-600 hover:text-red-600" data-testid={`user-delete-${u.id}`}><Trash size={16} /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {creating && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setCreating(false)}>
          <div className="bg-white max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="user-create-modal">
            <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
              <h2 className="font-semibold">Create admin user</h2>
              <button onClick={() => setCreating(false)} className="text-zinc-500 text-xl leading-none">×</button>
            </div>
            <div className="p-6 space-y-4">
              <Input label="Full name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="new-user-name" />
              <Input label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="new-user-email" />
              <Input label="Password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} testid="new-user-password" />
              <label className="block text-sm">
                <span className="font-semibold text-zinc-800 mb-1 block">Role</span>
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full border border-zinc-200 px-3 py-2 bg-white" data-testid="new-user-role">
                  {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </label>
            </div>
            <div className="px-6 py-4 border-t border-zinc-200 flex justify-end gap-2">
              <button onClick={() => setCreating(false)} className="btn-ghost-ink">Cancel</button>
              <button onClick={create} className="btn-signal" data-testid="new-user-save">Create</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Input({ label, value, onChange, testid }) {
  return (
    <label className="block text-sm">
      <span className="font-semibold text-zinc-800 mb-1 block">{label}</span>
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full border border-zinc-200 px-3 py-2 bg-white" data-testid={testid} />
    </label>
  );
}
